import { apiFetch } from "./api";

// ============================================================================
// DTOs (mirror src/Allmark.Contracts/ClientsAccounts + Common/PagedResult)
//
// Wire-format casing: ASP.NET Core System.Text.Json default = camelCase.
// Consecutive uppercase acronyms are lowercased: CCEmailAddress → ccEmailAddress.
// ============================================================================

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

// ----------------------------------------------------------------------------
// Clients
// ----------------------------------------------------------------------------

export interface ClientDto {
  clientId: number;
  fullName: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  companyId: number | null;
  phone: string | null;
  fax: string | null;
  mobilePhone: string | null;
  emailAddress: string | null;
  /** Maps to C# CCEmailAddress → camelCase wire: ccEmailAddress */
  ccEmailAddress: string | null;
  departmentName: string | null;
  deliveryAddressId: number | null;
  invoiceAddressId: number | null;
  hasApprovedCreditAccount: boolean;
  hasWebSiteLogin: boolean;
  lockCompanyName: boolean;
  showEmail: boolean;
  plainPackaging: boolean;
  summary: string | null;
  branch: string | null;
  createdOn: string | null;
  createdByName: string | null;
  deleted: boolean;
}

export interface ClientSummaryDto {
  clientId: number;
  fullName: string;
  companyName: string | null;
  emailAddress: string | null;
  phone: string | null;
  branch: string | null;
  hasApprovedCreditAccount: boolean;
  deleted: boolean;
}

export interface ClientFinderResultDto {
  clientId: number;
  fullName: string;
  emailAddress: string | null;
  companyId: number | null;
  companyName: string | null;
}

export interface CreateClientRequest {
  firstName: string;
  lastName: string;
  emailAddress?: string | null;
  phone?: string | null;
  mobilePhone?: string | null;
  companyId?: number | null;
  companyName?: string | null;
  departmentName?: string | null;
  branch?: string | null;
  lockCompanyName?: boolean;
  showEmail?: boolean;
  plainPackaging?: boolean;
  summary?: string | null;
}

export interface UpdateClientRequest {
  firstName: string;
  lastName: string;
  emailAddress: string | null;
  ccEmailAddress: string | null;
  phone: string | null;
  fax: string | null;
  mobilePhone: string | null;
  companyId: number | null;
  companyName: string | null;
  departmentName: string | null;
  branch: string | null;
  lockCompanyName: boolean;
  showEmail: boolean;
  plainPackaging: boolean;
  summary: string | null;
  deliveryAddressId: number | null;
  invoiceAddressId: number | null;
  hasApprovedCreditAccount: boolean;
  hasWebSiteLogin: boolean;
}

export interface ClientAddressDto {
  id: number;
  clientId: number | null;
  address: string;
  city: string | null;
  state: string;
  postCode: string | null;
  clientAddressNo: number | null;
  clientOwnsAddress: string | null;
  formattedAddress: string | null;
  deleted: boolean;
}

export interface CreateClientAddressRequest {
  address: string;
  city?: string | null;
  state?: string | null;
  postCode?: string | null;
  clientOwnsAddress?: string | null;
}

export interface UpdateClientAddressRequest {
  address: string;
  city: string | null;
  state: string;
  postCode: string | null;
  clientOwnsAddress: string | null;
}

export interface ClientAuditDto {
  auditRecId: number;
  clientId: number | null;
  createdOn: string;
  eventType: string;
  eventDescription: string | null;
  userName: string | null;
  ipAddress: string | null;
  pageUrl: string | null;
}

// ----------------------------------------------------------------------------
// Accounts
// ----------------------------------------------------------------------------

export interface AccountDto {
  accountId: number;
  accountReferenceNo: string;
  pymtReferenceNo: string;
  accountStatusCode: string;
  accountStatusDescription: string | null;
  companyId: number | null;
  companyName: string | null;
  clientId: number | null;
  clientFullName: string | null;
  accountAddressId: number | null;
  accountAddressFormatted: string | null;
  creditPeriodDays: number | null;
  stmtDeliveryMethod: string | null;
  emailAddress: string | null;
  /** Maps to C# CCEmailAddress → camelCase wire: ccEmailAddress */
  ccEmailAddress: string | null;
  currentBalanceOutstanding: number;
  currentStmtPeriodBalanceOwing: number;
  previousStmtPeriodBalanceOwing: number;
  pastStmtPeriodBalanceOwing: number;
  past2StmtPeriodBalanceOwing: number;
  openingBalance: number;
  myobBalanceOutstanding: number;
  lastStatementIssuedDate: string | null;
  lastInvoiceIssuedDate: string | null;
  lastPaymentDate: string | null;
  lastPaymentAmount: number | null;
  nextPaymentDueDate: string | null;
  nextPaymentAmountDue: number | null;
  termsAccepted: boolean;
  termsAcceptedOn: string | null;
  termsAcceptedId: number | null;
  currentStatusChangedOn: string | null;
  currentStatusChangedByEmployeeId: number | null;
  currentStatusReason: string | null;
  createdOn: string | null;
  createdByName: string | null;
  deleted: boolean;
  totalAdjustments: number | null;
  totalRefunds: number | null;
  totalWithdrawals: number | null;
  totalPaymentsThisFinYear: number | null;
  totalPaymentsLastFinYear: number | null;
  overdueCount: number | null;
}

export interface AccountSummaryDto {
  accountId: number;
  accountReferenceNo: string;
  accountStatusCode: string;
  companyName: string | null;
  clientFullName: string | null;
  currentBalanceOutstanding: number;
  totalOverdue: number | null;
  lastPaymentDate: string | null;
  lastStatementIssuedDate: string | null;
  nextPaymentDueDate: string | null;
  termsAccepted: boolean;
  stmtDeliveryMethod: string | null;
  deleted: boolean;
}

export interface CreateAccountRequest {
  companyId: number;
  clientId: number;
  accountAddressId: number;
  creditPeriodDays?: number | null;
  stmtDeliveryMethod?: string | null;
  emailAddress?: string | null;
  ccEmailAddress?: string | null;
}

export interface UpdateAccountRequest {
  accountAddressId?: number | null;
  creditPeriodDays?: number | null;
  stmtDeliveryMethod?: string | null;
  emailAddress?: string | null;
  ccEmailAddress?: string | null;
  openingBalance?: number | null;
  myobBalanceOutstanding?: number | null;
}

export interface ChangeAccountStatusRequest {
  newStatusCode: string;
  reason?: string | null;
}

export interface RecalculateBalanceResultDto {
  currentBalanceOutstanding: number;
  currentStmtPeriodBalanceOwing: number;
  previousStmtPeriodBalanceOwing: number;
  pastStmtPeriodBalanceOwing: number;
  past2StmtPeriodBalanceOwing: number;
}

// ----------------------------------------------------------------------------
// Account Transactions
// ----------------------------------------------------------------------------

export interface AccountTransactionDto {
  tranId: number;
  accountId: number;
  transactionDate: string;
  tranTypeCode: string;
  tranTypeDescription: string | null;
  isCredit: boolean;
  tranAmount: number;
  runningBalance: number;
  invoiceId: number | null;
  invoiceReferenceNo: string | null;
  comment: string | null;
  createdOn: string | null;
  createdByName: string | null;
  deleted: boolean;
}

export interface PostManualAccountTransactionRequest {
  tranTypeCode: string;
  amount: number;
  transactionDate: string;
  comment?: string | null;
}

export interface UpdateAccountTransactionRequest {
  comment: string | null;
}

// ----------------------------------------------------------------------------
// Account Notes
// ----------------------------------------------------------------------------

export interface AccountNoteDto {
  noteId: number;
  accountId: number;
  text: string;
  createdOn: string | null;
  createdByName: string | null;
  deleted: boolean;
}

export interface CreateAccountNoteRequest {
  text: string;
}

export interface UpdateAccountNoteRequest {
  text: string;
}

// ----------------------------------------------------------------------------
// Account Events
// ----------------------------------------------------------------------------

export interface AccountEventDto {
  accountEventId: number;
  accountId: number;
  eventCode: string;
  shortDesc: string;
  description: string | null;
  createdOn: string | null;
  createdByName: string | null;
}

// ============================================================================
// API helpers
// ============================================================================

export const clientsApi = {
  list:   (qs: string) =>
    apiFetch<PagedResult<ClientSummaryDto>>(`/api/clients?${qs}`),
  get:    (id: number) =>
    apiFetch<ClientDto>(`/api/clients/${id}`),
  create: (body: CreateClientRequest) =>
    apiFetch<ClientDto>("/api/clients", { method: "POST", body: JSON.stringify(body) }),
  patch:  (id: number, body: UpdateClientRequest) =>
    apiFetch<ClientDto>(`/api/clients/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  delete: (id: number) =>
    apiFetch<void>(`/api/clients/${id}`, { method: "DELETE" }),
  audit:  (id: number, page = 0) =>
    apiFetch<PagedResult<ClientAuditDto>>(`/api/clients/${id}/audit?page=${page}`),
  finder: (q: string, take = 10) =>
    apiFetch<ClientFinderResultDto[]>(`/api/clients/finder?q=${encodeURIComponent(q)}&take=${take}`),
};

export const clientAddressesApi = {
  list:   (clientId: number) =>
    apiFetch<ClientAddressDto[]>(`/api/clients/${clientId}/addresses`),
  create: (clientId: number, body: CreateClientAddressRequest) =>
    apiFetch<ClientAddressDto>(`/api/clients/${clientId}/addresses`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  patch:  (clientId: number, addrId: number, body: UpdateClientAddressRequest) =>
    apiFetch<ClientAddressDto>(`/api/clients/${clientId}/addresses/${addrId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  delete: (clientId: number, addrId: number) =>
    apiFetch<void>(`/api/clients/${clientId}/addresses/${addrId}`, { method: "DELETE" }),
};

export const accountsApi = {
  list:    (qs: string) =>
    apiFetch<PagedResult<AccountSummaryDto>>(`/api/accounts?${qs}`),
  get:     (id: number) =>
    apiFetch<AccountDto>(`/api/accounts/${id}`),
  create:  (body: CreateAccountRequest) =>
    apiFetch<AccountDto>("/api/accounts", { method: "POST", body: JSON.stringify(body) }),
  patch:   (id: number, body: UpdateAccountRequest) =>
    apiFetch<AccountDto>(`/api/accounts/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  delete:  (id: number) =>
    apiFetch<void>(`/api/accounts/${id}`, { method: "DELETE" }),
  status:  (id: number, body: ChangeAccountStatusRequest) =>
    apiFetch<AccountDto>(`/api/accounts/${id}/status`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  recalculateAging:      (id: number) =>
    apiFetch<RecalculateBalanceResultDto>(`/api/accounts/${id}/recalculate-aging`, { method: "POST" }),
  recalculateFromLedger: (id: number) =>
    apiFetch<RecalculateBalanceResultDto>(`/api/accounts/${id}/recalculate-from-ledger`, { method: "POST" }),
  events:  (id: number, page = 0) =>
    apiFetch<PagedResult<AccountEventDto>>(`/api/accounts/${id}/events?page=${page}`),
};

// ----------------------------------------------------------------------------
// Stmt delivery method (lookup)
// ----------------------------------------------------------------------------

export interface StmtDeliveryMethodOption {
  stmtDeliveryMethodCode: string;
  description: string;
  deleted: boolean;
}

export const listStmtDeliveryMethodLookups = (): Promise<StmtDeliveryMethodOption[]> =>
  apiFetch<PagedResult<StmtDeliveryMethodOption>>("/api/lookups/stmt-delivery-methods").then(
    (result) => result.items,
  );

export const accountTransactionsApi = {
  list:   (accountId: number, qs = "") =>
    apiFetch<PagedResult<AccountTransactionDto>>(
      `/api/accounts/${accountId}/transactions${qs ? `?${qs}` : ""}`,
    ),
  post:   (accountId: number, body: PostManualAccountTransactionRequest) =>
    apiFetch<AccountTransactionDto>(`/api/accounts/${accountId}/transactions`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  patch:  (tranId: number, body: UpdateAccountTransactionRequest) =>
    apiFetch<void>(`/api/account-transactions/${tranId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  delete: (tranId: number) =>
    apiFetch<void>(`/api/account-transactions/${tranId}`, { method: "DELETE" }),
};

export const accountNotesApi = {
  list:   (accountId: number, qs = "") =>
    apiFetch<PagedResult<AccountNoteDto>>(
      `/api/accounts/${accountId}/notes${qs ? `?${qs}` : ""}`,
    ),
  post:   (accountId: number, body: CreateAccountNoteRequest) =>
    apiFetch<AccountNoteDto>(`/api/accounts/${accountId}/notes`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  patch:  (noteId: number, body: UpdateAccountNoteRequest) =>
    apiFetch<void>(`/api/account-notes/${noteId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  delete: (noteId: number) =>
    apiFetch<void>(`/api/account-notes/${noteId}`, { method: "DELETE" }),
};

// Plan II — Statements
export interface AccountStatementSummaryDto {
  statementId: number;
  statementNo: string;
  statementDate: string;
  periodFromDate: string;
  periodToDate: string;
  openingBalance: number;
  closingBalance: number;
  issueMethod: string | null;
  issuedOn: string | null;
}
export interface AccountStatementDto extends AccountStatementSummaryDto {
  accountId: number;
  outstandingBalTypeStmt: boolean;
  issuedByName: string | null;
  pdfBlobPath: string | null;
  recipientEmailAddress: string | null;
  recipientCCEmailAddress: string | null;
}
export interface IssueStatementRequest {
  forDate: string;
  outstandingOverride: boolean | null;
  issueMethod: "Email" | "Print" | "Manual";
}
export interface EmailStatementRequest {
  toEmailAddress: string;
  ccEmailAddress: string | null;
  bccEmailAddress?: string | null;
  message?: string | null;
}

export const accountStatementsApi = {
  list: (accountId: number, qs = "") => apiFetch<PagedResult<AccountStatementSummaryDto>>(`/api/accounts/${accountId}/statements${qs ? `?${qs}` : ""}`),
  issue: (accountId: number, body: IssueStatementRequest) =>
    apiFetch<{ jobId: string }>(`/api/accounts/${accountId}/statements`, { method: "POST", body: JSON.stringify(body) }),
  get: (statementId: number) => apiFetch<AccountStatementDto>(`/api/account-statements/${statementId}`),
  pdfUrl: (statementId: number) => `/api/account-statements/${statementId}/pdf`,
  email: (statementId: number, body: EmailStatementRequest) =>
    apiFetch<void>(`/api/account-statements/${statementId}/email`, { method: "POST", body: JSON.stringify(body) }),
  regenerate: (statementId: number) =>
    apiFetch<{ ok?: boolean; warning?: string }>(`/api/account-statements/${statementId}/regenerate`, { method: "POST" }),
};

// Plan II — Terms
export interface AccountTermsSummaryDto { termsId: number; versionNumber: number; effectiveFrom: string; title: string; createdByName: string | null; accountsUsingCount: number; }
export interface AccountTermsDto extends AccountTermsSummaryDto { templateText: string; createdOn: string | null; modifiedOn: string | null; modifiedByName: string | null; acceptedOn: string | null; acceptedByName: string | null; acceptedViaIssuedLinkId: number | null; }
export interface CreateAccountTermsRequest { title: string; effectiveFrom: string; templateText: string; }
export interface SendTermsForAcceptanceRequest { termsId: number; overrideRecipientEmail: string | null; }
export interface AcceptTermsInternalRequest { termsId: number; acceptedByName: string; signatureText: string | null; }

export const accountTermsApi = {
  list: () => apiFetch<PagedResult<AccountTermsSummaryDto>>("/api/account-terms"),
  get: (termsId: number) => apiFetch<AccountTermsDto>(`/api/account-terms/${termsId}`),
  create: (body: CreateAccountTermsRequest) => apiFetch<AccountTermsDto>("/api/account-terms", { method: "POST", body: JSON.stringify(body) }),
  send: (accountId: number, body: SendTermsForAcceptanceRequest) =>
    apiFetch<void>(`/api/accounts/${accountId}/terms/send-for-acceptance`, { method: "POST", body: JSON.stringify(body) }),
  internalAccept: (accountId: number, body: AcceptTermsInternalRequest) =>
    apiFetch<void>(`/api/accounts/${accountId}/terms/accept`, { method: "POST", body: JSON.stringify(body) }),
};
